import type {
  AuthUser,
  LoginInput,
  RegisterInput,
  Canvas,
} from "@restack/shared";
import { appJotaiStore } from "../app-jotai";
import { authSessionAtom, authStorage } from "./auth-state";

const API_BASE = "/api";

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken?: string;
}

export interface UserMeResponse {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    createdAt?: string;
  };
}

export interface CanvasListResponse {
  canvases: Canvas[];
}

export interface CanvasDetailResponse {
  canvas: Canvas;
}

class ApiError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = "ApiError";
  }
}

let refreshPromise: Promise<AuthResponse | null> | null = null;

async function performTokenRefresh(): Promise<AuthResponse | null> {
  try {
    const storedRefreshToken = authStorage.getRefreshToken();
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(storedRefreshToken ? { refreshToken: storedRefreshToken } : {}),
    });

    if (!res.ok) {
      return null;
    }

    const data = (await res.json()) as AuthResponse;
    if (data?.accessToken && data?.user) {
      authStorage.save(data.accessToken, data.user, data.refreshToken);
      appJotaiStore.set(authSessionAtom, {
        user: data.user,
        accessToken: data.accessToken,
        isLoading: false,
        error: null,
      });
      return data;
    }
    return null;
  } catch (e) {
    console.error("Token auto-refresh failed", e);
    return null;
  }
}

async function request<T>(
  url: string,
  options: RequestInit & { _isRetry?: boolean } = {},
  token?: string | null,
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");

  const activeToken = token ?? authStorage.getAccessToken();
  if (activeToken) {
    headers.set("Authorization", `Bearer ${activeToken}`);
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
    credentials: "include", // Supports cookies alongside bearer tokens
  });

  // Handle expired/invalid token with transparent refresh & retry
  if (response.status === 401 && !options._isRetry && !url.startsWith("/auth/")) {
    if (!refreshPromise) {
      refreshPromise = performTokenRefresh().finally(() => {
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;
    if (refreshed?.accessToken) {
      // Retry original request with the fresh token
      return request<T>(url, { ...options, _isRetry: true }, refreshed.accessToken);
    } else {
      // Refresh failed completely - clear invalid session
      authStorage.clear();
      appJotaiStore.set(authSessionAtom, {
        user: null,
        accessToken: null,
        isLoading: false,
        error: "Session expired, please sign in again",
      });
    }
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const data = isJson ? await response.json() : null;

  if (!response.ok) {
    const errorMsg = data?.error || data?.message || `Request failed with status ${response.status}`;
    throw new ApiError(errorMsg, response.status);
  }

  return data as T;
}

export const authApi = {
  login: (data: LoginInput) =>
    request<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  register: (data: RegisterInput) =>
    request<AuthResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  refresh: (refreshToken?: string) =>
    request<AuthResponse>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    }),

  logout: (refreshToken?: string) =>
    request<{ success: boolean }>("/auth/logout", {
      method: "POST",
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    }),

  getMe: (token?: string | null) =>
    request<UserMeResponse>("/user/me", { method: "GET" }, token),
};

export const canvasApi = {
  list: (token?: string | null) =>
    request<CanvasListResponse>("/canvas", { method: "GET" }, token),

  get: (id: string, token?: string | null) =>
    request<CanvasDetailResponse>(`/canvas/${id}`, { method: "GET" }, token),

  create: (
    payload: {
      name: string;
      description?: string;
      data: any;
      thumbnail?: string;
      isPublic?: boolean;
    },
    token?: string | null,
  ) =>
    request<CanvasDetailResponse>(
      "/canvas",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
      token,
    ),

  update: (
    id: string,
    payload: {
      name?: string;
      description?: string;
      data?: any;
      thumbnail?: string;
      isPublic?: boolean;
    },
    token?: string | null,
  ) =>
    request<CanvasDetailResponse>(
      `/canvas/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
      token,
    ),

  delete: (id: string, token?: string | null) =>
    request<{ success: boolean }>(`/canvas/${id}`, { method: "DELETE" }, token),
};
